'use client';

import type { FC } from 'react';
import { Badge, P } from '@paypoteam/ui-components';
import Image from 'next/image';
import Link, { type LinkProps } from 'next/link';

import { shopReferral } from '@/lib/gtm';
import { mapBadgeColor } from '@/utils/mapBadgeColor';
import mapPrismicImageProxy from '@/utils/prismic/mapPrismicImageProxy';

import type { TileDataType } from './ProductTileTemplate.types';

export interface ProductTileTemplateProps extends Omit<LinkProps, 'href'> {
    tileData: TileDataType;
    shopLinkPlacement?: string;
}

const ProductTileTemplate: FC<ProductTileTemplateProps> = ({
    tileData,
    shopLinkPlacement,
    ...props
}) => {
    const {
        name,
        link,
        price,
        priceOld,
        promoTag,
        image,
        badgeText,
        badgeColor,
    } = tileData;

    return (
        <Link
            target="_blank"
            onClick={() => shopReferral(name, shopLinkPlacement)}
            href={link}
            {...props}
        >
            <div className="relative aspect-[1/1] max-h-[210px] w-full p-3 md:px-3 md:py-4">
                {badgeText ? (
                    <Badge
                        className="relative z-10 text-black"
                        variant={mapBadgeColor(badgeColor)}
                        size="m"
                    >
                        {badgeText}
                    </Badge>
                ) : null}

                {image.src && (
                    <Image
                        src={mapPrismicImageProxy(image.src)}
                        alt={image.alt}
                        fill
                        objectFit="cover"
                        className="rounded-2xl"
                    />
                )}
            </div>

            <div className="flex flex-col p-0 pb-4 pt-3 lg:p-3">
                <P className="text-2xs text-typo-primary md:text-sm">{name}</P>

                <P className="mt-3 flex flex-wrap text-2xs font-bold md:text-sm">
                    <span className="mr-2 text-typo-primary">{price}</span>

                    <span className="text-typo-disabled line-through">
                        {priceOld}
                    </span>
                </P>

                <P className="text-3xs text-typo-primary">{promoTag}</P>
            </div>
        </Link>
    );
};

export default ProductTileTemplate;
